import { Injectable } from '@angular/core';
import { gunzipSync, gzipSync } from 'fflate';

@Injectable({
	providedIn: 'root',
})
export class CompressionService {
	private readonly COMPRESSION_PREFIX = 'compressed:';
	private readonly COMPRESSION_THRESHOLD = 1024; // Only compress data larger than 1KB

	/**
	 * Synchronously compresses data using fflate
	 * @param data The data to compress
	 * @returns string The compressed data as an efficient string
	 */
	compressSync(data: string): string {
		try {
			// Convert string to Uint8Array
			const textEncoder = new TextEncoder();
			const uint8Array = textEncoder.encode(data);

			// Compress using fflate
			const compressed = gzipSync(uint8Array);

			// Convert to efficient string encoding
			const encodedString = this.uint8ArrayToString(compressed);
			return this.COMPRESSION_PREFIX + encodedString;
		} catch (error) {
			console.error('Synchronous compression failed:', error);
			return data; // Fallback to uncompressed
		}
	}

	/**
	 * Synchronously decompresses data using fflate
	 * @param compressedData The compressed data as a string
	 * @returns string The decompressed data
	 */
	decompressSync(compressedData: string): string {
		try {
			// Check if data is compressed
			if (!compressedData.startsWith(this.COMPRESSION_PREFIX)) {
				return compressedData; // Not compressed, return as-is
			}

			// Remove prefix and convert from efficient string encoding
			const encodedData = compressedData.substring(this.COMPRESSION_PREFIX.length);
			const uint8Array = this.stringToUint8Array(encodedData);

			// Decompress using fflate
			const decompressed = gunzipSync(uint8Array);

			// Convert to string
			const textDecoder = new TextDecoder();
			return textDecoder.decode(decompressed);
		} catch (error) {
			console.error('Synchronous decompression failed:', error);
			return compressedData; // Fallback to original data
		}
	}

	/**
	 * Determines if data should be compressed based on size
	 * @param data The data to check
	 * @returns boolean True if data should be compressed
	 */
	shouldCompress(data: string): boolean {
		return data.length > this.COMPRESSION_THRESHOLD;
	}

	/**
	 * Converts Uint8Array to a more efficient string representation
	 * This is more space-efficient than base64
	 */
	private uint8ArrayToString(uint8Array: Uint8Array): string {
		// Use a more efficient encoding than base64
		// Convert each byte to a character in a way that's more compact
		let result = '';
		for (let i = 0; i < uint8Array.length; i++) {
			// Use a range of characters that are safe for localStorage
			// Map 0-255 to 32-255 (printable ASCII characters)
			const charCode = uint8Array[i] + 32;
			result += String.fromCharCode(charCode);
		}
		return result;
	}

	/**
	 * Converts string back to Uint8Array
	 */
	private stringToUint8Array(str: string): Uint8Array {
		const uint8Array = new Uint8Array(str.length);
		for (let i = 0; i < str.length; i++) {
			// Reverse the mapping: subtract 32 from char code
			uint8Array[i] = str.charCodeAt(i) - 32;
		}
		return uint8Array;
	}
}
